import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuthStore, useCampaignsStore, useSettingsStore } from '@/stores';
import * as api from '@/lib/api';
import {
  getCampaignClients,
  saveCampaignClients,
  getAllCustomerStats,
  saveCustomerStatsBatch,
} from '@/lib/db';
import { getDaysAbsent, getDaysUntil } from '@/utils/date';
import {
  extractRelationalBookingEntities,
  extractRelationalSaleItemEntities,
} from '@/utils/relationalResolution';
import { groupClientsByDaysAbsent, groupClientsBySales, groupClientsByVisits } from '@/utils/mathGroups';
import type { CampaignClient, SortField, SortOrder, GroupBy } from '@/types/appointments';
import type { CampaignSegment, CustomerStatsCacheItem } from '@/stores/campaigns.store';
import type { Settings } from '@/types/settings';

const STATS_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days TTL

export function useCampaigns() {
  const { workspaceId, locationId } = useAuthStore();
  const settings = useSettingsStore();
  const {
    clients,
    loading,
    loadingPhase,
    loadedCount,
    totalCount,
    activeCampaignSegment,
    searchQuery,
    sortBy,
    sortOrder,
    groupBy,
    hideProcessed,
    statsProgress,
    setClients,
    updateClientsBatch,
    setLoading,
    setLoadingProgress,
    setActiveSegment,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setHideProcessed,
    markProcessed,
    unmarkProcessed,
    isProcessed,
    updateStatsCache,
    setStatsProgress,
  } = useCampaignsStore();

  const isFetchingRef = useRef(false);
  const isQueueRunningRef = useRef(false);

  const fetchCampaigns = useCallback(async (force = false) => {
    if (!workspaceId || isFetchingRef.current) return;
    if (!force && useCampaignsStore.getState().clients.length > 0) return;

    // Check Dexie if store is empty before network call to ensure 0ms render
    if (useCampaignsStore.getState().clients.length === 0) {
      try {
        const [cachedClients, cachedStats] = await Promise.all([
          getCampaignClients(workspaceId),
          getAllCustomerStats(),
        ]);
        if (cachedClients.length > 0) {
          setClients(cachedClients);
        }
        if (Object.keys(cachedStats).length > 0) {
          for (const [cid, item] of Object.entries(cachedStats)) {
            updateStatsCache(cid, item);
          }
        }
        if (!force && cachedClients.length > 0) {
          return;
        }
      } catch (cacheErr) {
        console.warn('Failed to read Dexie cache in fetchCampaigns:', cacheErr);
      }
    }

    isFetchingRef.current = true;
    setLoading(true);
    setLoadingProgress('retention', 0, 0);

    try {
      // 1. Calculate dynamic date range from settings.campaignRangeDays
      const rangeDays = settings.campaignRangeDays || 365;
      const dateTo = dayjs().format('YYYY-MM-DD');
      const dateFrom = dayjs().subtract(rangeDays, 'day').format('YYYY-MM-DD');

      const allRetentionRows: any[] = [];
      const limitRetention = 100;
      let offsetRetention = 0;
      let hasMoreRetention = true;

      // Paginate through ALL retention report pages in memory
      while (hasMoreRetention) {
        try {
          const res = await api.getCampaigns({
            workspaceId,
            limit: limitRetention,
            offset: offsetRetention,
            dateFrom,
            dateTo,
          });

          const resAny = res as any;
          const included = resAny?.included || resAny?.data?.included || [];
          const table = included.find(
            (inc: any) =>
              inc.type === 'report-tables' &&
              (inc.id === 'retention_list_table' || inc.id?.includes('retention'))
          );
          const rows = table?.attributes?.rows || [];
          const totalReportRows = table?.attributes?.['total-rows-count'] || 0;

          if (rows.length > 0) {
            allRetentionRows.push(...rows);
            setLoadingProgress('retention', allRetentionRows.length, totalReportRows || allRetentionRows.length);
          }

          if (rows.length === limitRetention && allRetentionRows.length < 2000) {
            offsetRetention += limitRetention;
          } else {
            hasMoreRetention = false;
          }
        } catch (e) {
          console.warn('Error fetching retention page at offset', offsetRetention, e);
          hasMoreRetention = false;
        }
      }

      // 2. Paginate through general customer search in memory
      setLoadingProgress('customers', 0, 0);
      const allCustomerNodes: any[] = [];
      const limitSearch = 50;
      let offsetSearch = 0;
      let hasMoreSearch = true;

      while (hasMoreSearch) {
        try {
          const custRes = await api.searchCustomers({
            workspaceId,
            limit: limitSearch,
            offset: offsetSearch,
          });

          const data = Array.isArray(custRes?.data) ? custRes.data : (custRes?.data?.data || []);
          const meta = (custRes as any)?.meta || {};
          const searchTotal = meta['results-count'] || meta['customers-count'] || 0;

          if (data.length > 0) {
            allCustomerNodes.push(...data);
            setLoadingProgress('customers', allCustomerNodes.length, searchTotal || allCustomerNodes.length);
          }

          if (data.length === limitSearch && allCustomerNodes.length < 1500) {
            offsetSearch += limitSearch;
          } else {
            hasMoreSearch = false;
          }
        } catch (e) {
          console.warn('Error searching customers page at offset', offsetSearch, e);
          hasMoreSearch = false;
        }
      }

      // 3. Map retention rows to CampaignClient domain models
      const currentCache = useCampaignsStore.getState().statsCache;
      const now = Date.now();

      const rawClients: CampaignClient[] = allRetentionRows.map((row: any, idx: number) => {
        const cells = row.cells || {};
        const name = cells.name?.value || row['customer-name'] || row.name || `Cliente #${idx + 1}`;
        const phone = cells.mobile_number?.value || cells.phone?.value || row['customer-phone'] || row.phone || '';
        const email = cells.email?.value || row['customer-email'] || row.email || '';
        const lastAppt = cells.last_appointment?.value || row['last-appointment-date'] || row.lastAppointmentDate || '';
        const parsedDays = Number(cells.days_absent?.value ?? row['days-absent'] ?? row.daysAbsent ?? 0);
        const daysAbsent = parsedDays > 0 ? parsedDays : (lastAppt ? getDaysAbsent(lastAppt) : 0);
        const totalAppts = Number(cells.total_appointments?.value ?? row['total-appointments'] ?? row.totalAppointments ?? 0);
        const totalSales = Number(cells.total_sales?.value ?? row['total-sales'] ?? row.totalSales ?? 0);
        const outstanding = Number(cells.outstanding_amount?.value ?? row['outstanding-amount'] ?? row.outstandingAmount ?? row.debt ?? 0);
        const customerId = cells.id?.value || row['customer-id'] || row.customerId || `cust-${idx}`;
        const staff = cells.staff?.value || row['last-staff-member'] || row.lastStaffMember || '';

        const cid = String(customerId);
        const cached = currentCache[cid];
        let nextAppt = '';
        let nextStaff = '';
        let nextService = '';
        let daysUntilNext: number | undefined = undefined;
        let lastService = '';
        let lastLoc = '';

        if (cached && typeof cached === 'object' && (now - (cached.lastFetch || 0) < STATS_CACHE_TTL_MS)) {
          nextAppt = cached.nextAppointment || '';
          nextStaff = cached.nextStaff || '';
          nextService = cached.nextServiceName || '';
          daysUntilNext = nextAppt ? getDaysUntil(nextAppt) : cached.daysUntilNext;
          lastService = cached.serviceName || '';
          lastLoc = cached.locationName || '';
        }

        return {
          id: customerId ? String(customerId) : `client-${idx}`,
          customerId: customerId ? String(customerId) : `cust-${idx}`,
          workspaceId,
          name,
          phone: String(phone || ''),
          email: String(email || ''),
          daysAbsent: lastAppt ? getDaysAbsent(lastAppt) : daysAbsent,
          lastAppointmentDate: lastAppt,
          lastStaffMember: staff,
          lastServiceName: lastService || undefined,
          lastLocationName: lastLoc || undefined,
          nextAppointmentDate: nextAppt || undefined,
          nextStaffMember: nextStaff || undefined,
          nextServiceName: nextService || undefined,
          daysUntilNext,
          totalAppointments: totalAppts,
          totalSales,
          outstandingAmount: outstanding,
          segment: 'lapsed' as const,
        };
      });

      // 4. Merge additional customers not in retention table
      const existingIds = new Set(rawClients.map((c) => String(c.customerId)));
      const statsQueueIds: string[] = [];

      allCustomerNodes.forEach((c: any, idx: number) => {
        const cid = String(c.id || '');
        if (cid && !existingIds.has(cid)) {
          existingIds.add(cid);
          const attrs = c.attributes || {};
          const name =
            attrs.name ||
            `${attrs['first-name'] || ''} ${attrs['last-name'] || ''}`.trim() ||
            `Cliente #${idx + 1}`;
          const phone = attrs['contact-number'] || attrs.telephone || '';
          const email = attrs.email || '';
          const sales = Number(attrs['total-sales-amount'] || 0);
          const debt = Number(attrs['outstanding-amount'] || attrs['outstanding-sales-amount'] || 0);

          let visits = 0;
          let lastAppt = '';
          let staff = '';
          let daysAbsent = 0;
          let lastServiceName = '';
          let lastLocationName = '';
          let nextAppt = '';
          let nextStaff = '';
          let nextServiceName = '';
          let daysUntilNext: number | undefined = undefined;

          const cached = currentCache[cid];
          if (cached && typeof cached === 'object' && (now - (cached.lastFetch || 0) < STATS_CACHE_TTL_MS)) {
            visits = cached.appointments || 0;
            lastAppt = cached.lastAppointment || '';
            staff = cached.staff || '';
            lastServiceName = cached.serviceName || '';
            lastLocationName = cached.locationName || '';
            daysAbsent = lastAppt ? getDaysAbsent(lastAppt) : (cached.daysAbsent ?? 0);
            nextAppt = cached.nextAppointment || '';
            nextStaff = cached.nextStaff || '';
            nextServiceName = cached.nextServiceName || '';
            daysUntilNext = nextAppt ? getDaysUntil(nextAppt) : cached.daysUntilNext;
          } else {
            statsQueueIds.push(cid);
          }

          rawClients.push({
            id: cid,
            customerId: cid,
            workspaceId,
            name,
            phone: String(phone || ''),
            email: String(email || ''),
            birthdayDay: attrs['birthday-day'] ?? null,
            birthdayMonth: attrs['birthday-month'] ?? null,
            createdAt: attrs['created-at'] || attrs.createdAt,
            daysAbsent,
            lastAppointmentDate: lastAppt,
            lastStaffMember: staff,
            lastServiceName: lastServiceName || undefined,
            lastLocationName: lastLocationName || undefined,
            nextAppointmentDate: nextAppt || undefined,
            nextStaffMember: nextStaff || undefined,
            nextServiceName: nextServiceName || undefined,
            daysUntilNext,
            totalAppointments: visits,
            totalSales: sales,
            outstandingAmount: debt,
            segment: 'lapsed' as const,
          });
        }
      });

      // Single atomic state update for the complete dataset
      setClients(rawClients);
      setLoadingProgress('idle', rawClients.length, rawClients.length);

      // Persist rawClients to Dexie
      try {
        await saveCampaignClients(rawClients);
      } catch (saveErr) {
        console.warn('Failed to save campaign clients to Dexie:', saveErr);
      }

      // 5. Run background queue to hydrate stats in batch
      if (statsQueueIds.length > 0 && !isQueueRunningRef.current) {
        isQueueRunningRef.current = true;
        processDeepStatsQueue(
          statsQueueIds,
          workspaceId,
          locationId,
          updateStatsCache,
          updateClientsBatch,
          setStatsProgress
        ).finally(() => {
          isQueueRunningRef.current = false;
        });
      }
    } catch (err) {
      console.error('Failed to fetch campaigns data:', err);
    } finally {
      setLoading(false);
      setLoadingProgress('idle', 0, 0);
      isFetchingRef.current = false;
    }
  }, [
    workspaceId,
    locationId,
    settings.campaignRangeDays,
    setClients,
    updateClientsBatch,
    setLoading,
    setLoadingProgress,
    updateStatsCache,
    setStatsProgress,
  ]);

  // Initial hydration from Dexie on mount / workspaceId change
  useEffect(() => {
    if (!workspaceId) return;
    const activeWsId = workspaceId;
    let isMounted = true;

    async function hydrateFromDexie() {
      try {
        const [cachedClients, cachedStats] = await Promise.all([
          getCampaignClients(activeWsId),
          getAllCustomerStats(),
        ]);
        if (!isMounted) return;

        if (cachedClients.length > 0) {
          setClients(cachedClients);
        }
        if (Object.keys(cachedStats).length > 0) {
          for (const [cid, item] of Object.entries(cachedStats)) {
            updateStatsCache(cid, item);
          }
        }
      } catch (err) {
        console.warn('Failed to hydrate campaigns from Dexie:', err);
      }
    }

    hydrateFromDexie();

    return () => {
      isMounted = false;
    };
  }, [workspaceId, setClients, updateStatsCache]);

  useEffect(() => {
    if (clients.length === 0 && workspaceId) {
      fetchCampaigns(false);
    }
  }, [clients.length, workspaceId, fetchCampaigns]);

  // Compute dynamic segment counts
  const totalSegmentCounts: Record<CampaignSegment, number> = useMemo(() => ({
    lapsed: clients.filter((c) => isClientLapsed(c, settings)).length,
    vip: clients.filter((c) => isClientVip(c, settings)).length,
    debtor: clients.filter((c) => isClientDebtor(c, settings)).length,
  }), [clients, settings]);

  const pendingSegmentCounts: Record<CampaignSegment, number> = useMemo(() => ({
    lapsed: clients.filter((c) => isClientLapsed(c, settings) && !isProcessed(c.id)).length,
    vip: clients.filter((c) => isClientVip(c, settings) && !isProcessed(c.id)).length,
    debtor: clients.filter((c) => isClientDebtor(c, settings) && !isProcessed(c.id)).length,
  }), [clients, settings, isProcessed]);

  // Filter clients for the active segment with hideProcessed support
  const activeClients = useMemo(() => {
    return clients
      .filter((c) => {
        if (hideProcessed && isProcessed(c.id)) return false;
        if (activeCampaignSegment === 'lapsed') return isClientLapsed(c, settings);
        if (activeCampaignSegment === 'vip') return isClientVip(c, settings);
        if (activeCampaignSegment === 'debtor') return isClientDebtor(c, settings);
        return true;
      })
      .map((c) => ({
        ...c,
        segment: activeCampaignSegment,
      }));
  }, [clients, activeCampaignSegment, settings, hideProcessed, isProcessed]);

  // Smart tab switching with automatic natural sorting
  const handleSelectSegment = useCallback((segment: CampaignSegment) => {
    setActiveSegment(segment);
    if (segment === 'lapsed') {
      setSortBy('daysAbsent');
      setSortOrder('desc');
    } else if (segment === 'vip') {
      setSortBy('totalSales');
      setSortOrder('desc');
    } else if (segment === 'debtor') {
      setSortBy('outstandingAmount');
      setSortOrder('desc');
    }
  }, [setActiveSegment, setSortBy, setSortOrder]);

  const filteredClients = getFilteredClients(activeClients, searchQuery);
  const sortedClients = getSortedClients(filteredClients, sortBy, sortOrder);
  const groupedClients = getGroupedClients(sortedClients, groupBy, settings);

  return {
    clients: sortedClients,
    groupedClients,
    totalClientsCount: clients.length,
    segmentCounts: hideProcessed ? pendingSegmentCounts : totalSegmentCounts,
    totalSegmentCounts,
    pendingSegmentCounts,
    loading,
    loadingPhase,
    loadedCount,
    totalCount,
    activeCampaignSegment,
    searchQuery,
    sortBy,
    sortOrder,
    groupBy,
    hideProcessed,
    statsProgress,
    fetchCampaigns: () => fetchCampaigns(true),
    setActiveSegment: handleSelectSegment,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setHideProcessed,
    markProcessed,
    unmarkProcessed,
    isProcessed,
  };
}

export function isClientLapsed(client: CampaignClient, settings: Settings): boolean {
  if (client.nextAppointmentDate) return false;
  const threshold = settings.inactivityDays || 60;
  return typeof client.daysAbsent === 'number' && client.daysAbsent >= threshold;
}

export function isClientVip(client: CampaignClient, settings: Settings): boolean {
  const minAppts = settings.vipMinAppointments || 10;
  const minSales = settings.vipMinSales || 250;
  const relation = settings.vipRelation || 'or';

  const hasAppts = (client.totalAppointments || 0) >= minAppts;
  const hasSales = (client.totalSales || 0) >= minSales;

  return relation === 'and' ? hasAppts && hasSales : hasAppts || hasSales;
}

export function isClientDebtor(client: CampaignClient, settings: Settings): boolean {
  const threshold = settings.debtorMinAmount || 0.01;
  return (client.outstandingAmount || 0) >= threshold;
}

async function processDeepStatsQueue(
  queueIds: string[],
  workspaceId: string,
  locationId: string | null,
  onStatsUpdate: (id: string, item: CustomerStatsCacheItem) => void,
  onBatchUpdate: (updatesMap: Record<string, Partial<CampaignClient>>) => void,
  onProgress: (current: number, total: number) => void
) {
  const batchSize = 6;
  const total = queueIds.length;
  let current = 0;
  const accumulatedUpdates: Record<string, Partial<CampaignClient>> = {};

  for (let i = 0; i < queueIds.length; i += batchSize) {
    const batch = queueIds.slice(i, i + batchSize);
    const statsBatch: (CustomerStatsCacheItem & { customerId: string })[] = [];

    await Promise.allSettled(
      batch.map(async (cid) => {
        try {
          const [statsRes, apptsRes] = await Promise.all([
            api.getCustomerStats(cid, workspaceId).catch(() => null),
            api.getCustomerAppointments(cid, workspaceId, locationId || '').catch(() => null),
          ]);

          const stats = statsRes?.data?.data?.attributes || statsRes?.data?.attributes || {};
          const count = Number(stats['completed-bookings-count'] ?? stats.completedBookingsCount ?? 0);
          const outstandingSales = Number(stats['outstanding-sales'] ?? stats.outstandingSales ?? 0);
          const totalSales = Number(stats['total-sales'] ?? stats.totalSales ?? 0);

          const now = dayjs().startOf('day');

          let nextApptDate = '';
          let nextStaff = '';
          let nextService = '';
          let daysUntilNext: number | undefined = undefined;

          let lastApptDate = '';
          let lastStaff = '';
          let lastService = '';
          let lastLocation = '';
          let daysAbsent: number | undefined = undefined;

          const apptsData = apptsRes?.data?.data || apptsRes?.data || [];
          if (Array.isArray(apptsData)) {
            for (const appt of apptsData) {
              const attr = appt?.attributes || {};
              const firstBooking = attr.bookings?.[0] || {};
              const dateStr =
                firstBooking['start-wall']?.date ||
                attr['start-wall']?.date ||
                attr['start-date'] ||
                '';
              if (!dateStr) continue;

              const staff =
                firstBooking.employee?.name || attr.employee?.name || '';
              const service =
                firstBooking.service?.name ||
                firstBooking['service-name'] ||
                attr.service?.name ||
                attr['service-name'] ||
                '';
              const locName =
                firstBooking.location?.name ||
                firstBooking['location-name'] ||
                attr.location?.name ||
                attr['location-name'] ||
                '';

              const apptDate = dayjs(dateStr).startOf('day');

              if (apptDate.isSameOrAfter(now)) {
                // Future appointment: pick closest upcoming
                if (!nextApptDate || apptDate.isBefore(dayjs(nextApptDate).startOf('day'))) {
                  nextApptDate = dateStr;
                  nextStaff = staff;
                  nextService = service;
                  daysUntilNext = getDaysUntil(dateStr);
                }
              } else {
                // Past visit: pick most recent
                if (!lastApptDate || apptDate.isAfter(dayjs(lastApptDate).startOf('day'))) {
                  lastApptDate = dateStr;
                  lastStaff = staff;
                  lastService = service;
                  lastLocation = locName;
                  daysAbsent = getDaysAbsent(dateStr);
                }
              }
            }
          }

          // Fallback to /sales if no past visit was found in appointments
          if (!lastApptDate) {
            const salesRes = await api.getCustomerSales(cid, workspaceId).catch(() => null);
            const salesData = salesRes?.data?.data || salesRes?.data || [];
            if (Array.isArray(salesData) && salesData.length > 0) {
              const firstSale = salesData[0];
              const salesIncluded = salesRes?.data?.included || (salesRes as any)?.included || [];
              const saleAttrs = firstSale?.attributes || {};
              const invoiceDate = saleAttrs['invoice-date'] || saleAttrs['created-at'] || '';
              if (invoiceDate) {
                lastApptDate = invoiceDate;
                daysAbsent = getDaysAbsent(invoiceDate);
              }
              lastLocation = saleAttrs['location-name'] || saleAttrs.location?.name || lastLocation;

              const saleEntities = extractRelationalSaleItemEntities(firstSale, salesIncluded);
              if (saleEntities.service) lastService = saleEntities.service;
              if (saleEntities.staff) lastStaff = saleEntities.staff;

              if (saleEntities.bookingId) {
                const bookingRes = await api.getBookingDetail(saleEntities.bookingId, workspaceId).catch(() => null);
                const resolved = extractRelationalBookingEntities(bookingRes, saleEntities.saleItem);
                if (resolved.staff) lastStaff = resolved.staff;
                if (resolved.service) lastService = resolved.service;
                if (resolved.location) lastLocation = resolved.location;
                if (resolved.appointmentDate) {
                  lastApptDate = resolved.appointmentDate;
                  daysAbsent = getDaysAbsent(resolved.appointmentDate);
                }
              }
            }
          }

          const cacheItem: CustomerStatsCacheItem = {
            appointments: count,
            lastAppointment: lastApptDate || undefined,
            staff: lastStaff || undefined,
            serviceName: lastService || undefined,
            locationName: lastLocation || undefined,
            nextAppointment: nextApptDate || undefined,
            nextStaff: nextStaff || undefined,
            nextServiceName: nextService || undefined,
            daysUntilNext,
            daysAbsent,
            outstandingSales,
            totalSales,
            lastFetch: Date.now(),
          };

          statsBatch.push({ ...cacheItem, customerId: cid });
          onStatsUpdate(cid, cacheItem);
          accumulatedUpdates[cid] = {
            totalAppointments: count,
            ...(outstandingSales > 0 && { outstandingAmount: outstandingSales }),
            ...(totalSales > 0 && { totalSales }),
            ...(lastApptDate && { lastAppointmentDate: lastApptDate }),
            ...(lastStaff && { lastStaffMember: lastStaff }),
            ...(lastLocation && { lastLocationName: lastLocation }),
            ...(lastService && { lastServiceName: lastService }),
            ...(daysAbsent !== undefined && { daysAbsent }),
            ...(nextApptDate && { nextAppointmentDate: nextApptDate }),
            ...(nextStaff && { nextStaffMember: nextStaff }),
            ...(nextService && { nextServiceName: nextService }),
            ...(daysUntilNext !== undefined && { daysUntilNext }),
          };
        } catch {
          // Ignore individual customer stat failure
        }
      })
    );

    // Save batch of customer stats to Dexie
    if (statsBatch.length > 0) {
      try {
        await saveCustomerStatsBatch(statsBatch);
      } catch (dbErr) {
        console.warn('Failed to save customer stats batch to Dexie:', dbErr);
      }
    }

    current += batch.length;
    onProgress(Math.min(current, total), total);

    // Apply batch updates periodically (every 18 clients) to minimize re-renders and persist updated clients
    if (Object.keys(accumulatedUpdates).length >= 18 || i + batchSize >= queueIds.length) {
      const updatesToFlush = { ...accumulatedUpdates };
      onBatchUpdate(updatesToFlush);

      try {
        const allClients = useCampaignsStore.getState().clients;
        const updatedClients = allClients.filter(
          (c) => updatesToFlush[c.id] || updatesToFlush[c.customerId]
        );
        if (updatedClients.length > 0) {
          await saveCampaignClients(updatedClients);
        }
      } catch (dbErr) {
        console.warn('Failed to update campaign clients in Dexie:', dbErr);
      }
    }

    // Throttle slightly to respect network & device
    if (i + batchSize < queueIds.length) {
      await new Promise((r) => setTimeout(r, 120));
    }
  }

  // Final flush
  if (Object.keys(accumulatedUpdates).length > 0) {
    onBatchUpdate(accumulatedUpdates);
    try {
      const allClients = useCampaignsStore.getState().clients;
      const updatedClients = allClients.filter(
        (c) => accumulatedUpdates[c.id] || accumulatedUpdates[c.customerId]
      );
      if (updatedClients.length > 0) {
        await saveCampaignClients(updatedClients);
      }
    } catch (dbErr) {
      console.warn('Failed to final save campaign clients in Dexie:', dbErr);
    }
  }
}

function getFilteredClients(clients: CampaignClient[], query: string): CampaignClient[] {
  if (!query) return clients;
  const q = query.toLowerCase().trim();
  return clients.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.lastStaffMember?.toLowerCase().includes(q)
  );
}

function getSortedClients(
  clients: CampaignClient[],
  sortBy: SortField,
  sortOrder: SortOrder
): CampaignClient[] {
  return [...clients].sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'daysAbsent':
        cmp = (a.daysAbsent || 0) - (b.daysAbsent || 0);
        break;
      case 'totalSales':
        cmp = (a.totalSales || 0) - (b.totalSales || 0);
        break;
      case 'totalAppointments':
        cmp = (a.totalAppointments || 0) - (b.totalAppointments || 0);
        break;
      case 'outstandingAmount':
        cmp = (a.outstandingAmount || 0) - (b.outstandingAmount || 0);
        break;
      case 'name':
      default:
        cmp = a.name.localeCompare(b.name);
    }
    return sortOrder === 'desc' ? -cmp : cmp;
  });
}

function getGroupedClients(
  clients: CampaignClient[],
  groupBy: GroupBy,
  settings: Settings
): Record<string, CampaignClient[]> {
  if (groupBy === 'none') return { '': clients };

  if (groupBy === 'days_absent_range') {
    return groupClientsByDaysAbsent(clients, settings.inactivityDays || 60);
  }

  if (groupBy === 'sales_range') {
    return groupClientsBySales(clients, settings.currencyCode || 'USD', settings.vipMinSales || 250);
  }

  if (groupBy === 'visits_range') {
    return groupClientsByVisits(clients, settings.vipMinAppointments || 10);
  }

  if (groupBy === 'debt_status') {
    const minDebt = settings.debtorMinAmount || 1;
    const groups = new Map<string, { label: string; list: CampaignClient[]; orderKey: number }>();

    for (const client of clients) {
      const debt = Number(client.outstandingAmount || 0);
      const withDebt = debt >= minDebt;
      const key = withDebt ? 'with_debt' : 'no_debt';
      const label = withDebt ? 'Estado de Deuda: Con saldo deudor' : 'Estado de Deuda: Al día (Sin deuda)';
      const orderKey = withDebt ? 1 : 2;

      if (!groups.has(key)) groups.set(key, { label, list: [], orderKey });
      groups.get(key)!.list.push(client);
    }

    const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
    const result: Record<string, CampaignClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  if (groupBy === 'staff' || groupBy === 'employee') {
    const groups = new Map<string, { label: string; list: CampaignClient[]; orderKey: number }>();
    for (const client of clients) {
      const staff = client.lastStaffMember || 'Sin asignar';
      const label = `Último Profesional: ${staff}`;
      const orderKey = staff === 'Sin asignar' ? 99 : 1;
      if (!groups.has(staff)) groups.set(staff, { label, list: [], orderKey });
      groups.get(staff)!.list.push(client);
    }
    const sorted = [...groups.entries()].sort((a, b) => {
      if (a[1].orderKey !== b[1].orderKey) return a[1].orderKey - b[1].orderKey;
      return a[1].label.localeCompare(b[1].label);
    });
    const result: Record<string, CampaignClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  return { '': clients };
}
